import React from 'react';
import {
  Box, Card, CardContent, Chip, Grid,
  IconButton, Tooltip, Typography,
} from '@mui/material';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

function avatarColor(name) {
  var colors = [
    { bg: '#EFF6FF', text: '#1D4ED8' },
    { bg: '#F0FDF4', text: '#15803D' },
    { bg: '#FFF7ED', text: '#C2410C' },
    { bg: '#FDF4FF', text: '#7E22CE' },
    { bg: '#F0F9FF', text: '#0369A1' },
    { bg: '#FFF1F2', text: '#BE123C' },
    { bg: '#F7FEE7', text: '#4D7C0F' },
    { bg: '#FEF3C7', text: '#92400E' },
  ];
  var n = name || '';
  var sum = 0;
  for (var i = 0; i < n.length; i++) { sum += n.charCodeAt(i); }
  return colors[sum % colors.length];
}

function initials(name) {
  if (!name) return '?';
  var words = name.trim().split(/\s+/);
  return words.slice(0, 2).map(function(w) {
    return w.charAt(0).toUpperCase();
  }).join('');
}

function PartnerCard(props) {
  var partner = props.partner;
  var color = avatarColor(partner.name);
  var countryName = (partner.country && partner.country.name) ? partner.country.name : '—';

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s',
        cursor: 'pointer',
        '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.10)', borderColor: '#D1D5DB' },
      }}
      onClick={function() { props.viewPartner(partner); }}
    >
      <CardContent sx={{
        p: 2, pb: '12px !important',
        flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5,
      }}>

        {/* Header */}
        <Box sx={{
          display: 'flex', alignItems: 'flex-start',
          justifyContent: 'space-between', gap: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box sx={{
              width: 38, height: 38, borderRadius: 2,
              bgcolor: color.bg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
              fontWeight: 700, fontSize: 14, color: color.text,
              letterSpacing: 0.5,
            }}>
              {initials(partner.name)}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{
                fontWeight: 700, fontSize: 13, color: '#111827',
                wordBreak: 'break-word', lineHeight: 1.3,
              }}>
                {partner.name}
              </Typography>
              {partner.code && (
                <Typography variant="caption" sx={{
                  fontSize: 11, color: '#9CA3AF', fontFamily: 'monospace',
                }}>
                  {partner.code}
                </Typography>
              )}
            </Box>
          </Box>
          <Tooltip title="View Partner">
            <IconButton
              size="small"
              onClick={function(e) {
                e.stopPropagation();
                props.viewPartner(partner);
              }}
              sx={{ color: '#6B7280', p: 0.5, flexShrink: 0 }}
            >
              <OpenInNewOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Meta row */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {countryName !== '—' && (
            <Chip label={countryName} size="small" sx={{
              height: 20, fontSize: 10,
              bgcolor: '#F0F9FF', color: '#0369A1',
              border: '1px solid #BAE6FD',
            }} />
          )}
          {partner.insurer && (
            <Chip label={partner.insurer} size="small" sx={{
              height: 20, fontSize: 10,
              bgcolor: '#F5F3FF', color: '#6D28D9',
              border: '1px solid #DDD6FE',
            }} />
          )}
          {partner.active !== undefined && (
            <Chip
              label={partner.active ? 'Active' : 'Inactive'}
              size="small"
              sx={{
                height: 20, fontSize: 10, fontWeight: 600,
                bgcolor: partner.active ? '#F0FDF4' : '#FEF2F2',
                color: partner.active ? '#15803D' : '#B91C1C',
                border: partner.active ? '1px solid #BBF7D0' : '1px solid #FECACA',
              }}
            />
          )}
        </Box>

      </CardContent>
    </Card>
  );
}

export default function PartnersTable(props) {
  var partners = props.partners || [];

  if (partners.length === 0) {
    return (
      <Box sx={{
        textAlign: 'center', py: 8,
        border: '1px dashed #E5E7EB', borderRadius: 2,
      }}>
        <BusinessOutlinedIcon sx={{ fontSize: 48, color: '#D1D5DB', mb: 1.5 }} />
        <Typography sx={{ fontSize: 15, color: '#6B7280', fontWeight: 500 }}>
          No partners found
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#9CA3AF', mt: 0.5 }}>
          Click &quot;+ Add Partner&quot; to add the first partner
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {partners.map(function(partner) {
        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={partner.guid || partner.code}>
            <PartnerCard
              partner={partner}
              viewPartner={props.viewPartner}
            />
          </Grid>
        );
      })}
    </Grid>
  );
}
